import type { Entity } from '../components'
import type { GameWorld } from '../world'
import { triggerEyeAlert, clearEyeAlert } from './eyes'
import { playSfx, panFor } from '../../engine/audio'
import type { EventBus } from '../events'
import { playerCanEat } from '../growth'
import { T } from '../tunables'

export function predatorAISystem(world: GameWorld, player: Entity, dt: number, elapsed: number, events: EventBus): void {
  const predators = world.with('predator', 'velocity', 'position')
  // Bouquet makes the player more conspicuous; predators detect from farther.
  const detectionMult = player.mods?.detectionRadiusMult ?? 1

  for (const e of predators) {
    const p = e.predator

    p.windupCooldown = Math.max(0, p.windupCooldown - dt)

    if (p.state === 'windup') {
      p.stateTimer -= dt
      // Freeze movement — direction is already committed in lungeDir.
      e.velocity.x *= 0.6
      e.velocity.y *= 0.6
      if (p.stateTimer <= 0) {
        p.state = 'lunge'
        p.stateTimer = T.predator.lungeDuration
        e.velocity.x = p.lungeDir.x * p.speed * T.predator.lungeSpeedMult
        e.velocity.y = p.lungeDir.y * p.speed * T.predator.lungeSpeedMult
      }
      continue
    }

    if (p.state === 'lunge') {
      p.stateTimer -= dt
      if (p.stateTimer <= 0) {
        p.state = 'recovery'
        p.stateTimer = T.predator.recoveryDuration
      }
      continue
    }

    if (p.state === 'recovery') {
      p.stateTimer -= dt
      e.velocity.x *= T.predator.recoverySpeedMult
      e.velocity.y *= T.predator.recoverySpeedMult
      if (p.stateTimer <= 0) {
        p.state = 'chase'
        p.windupCooldown = T.predator.windupCooldown
        clearEyeAlert(e)
      }
      continue
    }

    // p.state === 'chase': existing chase / wander / separation logic.
    const dx = player.position.x - e.position.x
    const dy = player.position.y - e.position.y
    const dist2 = dx * dx + dy * dy
    const det = p.detectionRadius * detectionMult

    if (dist2 < det * det) {
      const dist = Math.sqrt(dist2)
      const nx = dx / dist
      const ny = dy / dist

      // Reversal legibility: a predator the player can eat doesn't hunt — it
      // runs. Seeing what chased you flee IS the power-curve payoff. Apex
      // predators (monorail-228) are the exception — they keep hunting at any
      // player size, so max-size runs still carry real danger. Their crimson
      // apex tint is the "this one still bites" tell.
      if (playerCanEat(player, e) && !p.apex) {
        const flee = p.speed * T.predator.fleeSpeedMult
        e.velocity.x += (-nx * flee - e.velocity.x) * Math.min(1, 4 * dt)
        e.velocity.y += (-ny * flee - e.velocity.y) * Math.min(1, 4 * dt)
        clearEyeAlert(e)
        continue
      }

      const speed = p.speed
      e.velocity.x += (nx * speed - e.velocity.x) * Math.min(1, 4 * dt)
      e.velocity.y += (ny * speed - e.velocity.y) * Math.min(1, 4 * dt)

      // Windup trigger: player within lunge range and cooldown elapsed.
      if (dist2 < T.predator.lungeRange * T.predator.lungeRange && p.windupCooldown === 0) {
        p.state = 'windup'
        p.stateTimer = p.windupDuration ?? T.predator.windupDuration
        p.lungeDir.x = nx
        p.lungeDir.y = ny
        playSfx('lunge_tick', { volume: 0.4, pan: panFor(e.position.x, player.position.x) })
        triggerEyeAlert(e)
        events.emit({ type: 'predator.windup', predator: e })
      }
    } else {
      const s = e.wander?.noiseSeed ?? 0
      e.velocity.x += Math.sin(elapsed * 0.9 + s) * T.predator.wanderDriftAmp * dt
      e.velocity.y += Math.cos(elapsed * 0.7 + s * 1.3) * T.predator.wanderDriftAmp * dt
      e.velocity.x -= e.velocity.x * T.predator.wanderDamping * dt
      e.velocity.y -= e.velocity.y * T.predator.wanderDamping * dt
    }

    // Separation: push away from nearby predators
    for (const other of predators) {
      if (other === e) continue
      const sdx = e.position.x - other.position.x
      const sdy = e.position.y - other.position.y
      const sep = p.separation
      const sdist2 = sdx * sdx + sdy * sdy
      if (sdist2 < sep * sep && sdist2 > 0) {
        const sdist = Math.sqrt(sdist2)
        const strength = (1 - sdist / sep) * 0.15
        e.velocity.x += (sdx / sdist) * strength
        e.velocity.y += (sdy / sdist) * strength
      }
    }
  }
}
